/**
 * Grava o microfone em partes independentes.
 *
 * `MediaRecorder` com `timeslice` não serve: só o primeiro pedaço traz o
 * cabeçalho do WebM, e o segundo sozinho não se transcreve. Aqui cada parte é
 * uma gravação inteira — ao fim do tempo, o gravador para e outro começa sobre
 * o mesmo microfone, com uma emenda de milissegundos.
 *
 * O tempo da parte conta só enquanto grava: pausar não fecha parte.
 */

export interface OpcoesDoGravador {
    parteMs: number;
    /** Chamado com cada parte fechada, na ordem. */
    aoFecharParte: (parte: { ordem: number; segundos: number; audio: Blob }) => Promise<void> | void;
    /** O microfone caiu (outro app, fone desconectado, permissão revogada). */
    aoPerderMicrofone?: () => void;
    /** Relógio injetável para os testes. */
    agora?: () => number;
}

/** Abaixo disto é estalo de botão, não fala. */
const MENOR_PARTE_BYTES = 1000;
const MENOR_PARTE_SEGUNDOS = 1;
const ESPERA_DO_FIM_MS = 2000;

export function microfoneSuportado(): boolean {
    return typeof MediaRecorder !== 'undefined'
        && typeof navigator !== 'undefined'
        && typeof navigator.mediaDevices?.getUserMedia === 'function';
}

function melhorFormato(): string {
    if (typeof MediaRecorder === 'undefined') return '';
    return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
        .find((t) => MediaRecorder.isTypeSupported?.(t)) ?? '';
}

/** A frase que a pessoa entende quando o navegador recusa o microfone. */
export function erroDoMicrofone(e: unknown): Error {
    const nome = typeof e === 'object' && e !== null && 'name' in e ? String((e as { name: unknown }).name) : '';
    if (nome === 'NotAllowedError' || nome === 'SecurityError') return new Error('Permita o acesso ao microfone nas configurações do navegador para gravar.');
    if (nome === 'NotFoundError' || nome === 'OverconstrainedError') return new Error('Nenhum microfone encontrado neste aparelho.');
    if (nome === 'NotReadableError' || nome === 'AbortError') return new Error('O microfone está sendo usado por outro app.');
    return e instanceof Error && e.message ? e : new Error('Não consegui ligar o microfone.');
}

export class GravadorEmPartes {
    private fluxo: MediaStream | null = null;
    private gravador: MediaRecorder | null = null;
    private pedacos: Blob[] = [];
    private ordem = 0;
    /** Milissegundos gravados na parte atual, sem as pausas. */
    private acumulado = 0;
    private desde: number | null = null;
    private relogio: ReturnType<typeof setInterval> | null = null;
    private fila: Promise<void> = Promise.resolve();
    private readonly agora: () => number;
    pausado = false;
    ativo = false;

    constructor(private readonly opcoes: OpcoesDoGravador) {
        this.agora = opcoes.agora ?? (() => Date.now());
    }

    /** Liga o microfone e começa a parte `ordemInicial`. */
    async iniciar(ordemInicial = 0): Promise<void> {
        if (this.ativo) return;
        let fluxo: MediaStream;
        try {
            fluxo = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
        } catch (e) {
            throw erroDoMicrofone(e);
        }
        this.fluxo = fluxo;
        fluxo.getAudioTracks().forEach((t) => {
            t.onended = () => {
                if (!this.ativo) return;
                void this.concluir().finally(() => this.opcoes.aoPerderMicrofone?.());
            };
        });
        this.ordem = ordemInicial;
        this.ativo = true;
        this.pausado = false;
        this.comecarParte();
        this.relogio = setInterval(() => this.conferirTempo(), 1000);
    }

    /** Segundos gravados na parte atual. */
    segundosDaParte(): number {
        const corrente = this.desde === null ? 0 : this.agora() - this.desde;
        return (this.acumulado + corrente) / 1000;
    }

    pausar(): void {
        if (!this.ativo || this.pausado) return;
        this.pausado = true;
        if (this.desde !== null) this.acumulado += this.agora() - this.desde;
        this.desde = null;
        try {
            if (this.gravador?.state === 'recording') this.gravador.pause();
        } catch {
            /* o navegador sem pausa segue gravando; o tempo já parou de contar */
        }
    }

    retomar(): void {
        if (!this.ativo || !this.pausado) return;
        this.pausado = false;
        this.desde = this.agora();
        try {
            if (this.gravador?.state === 'paused') this.gravador.resume();
        } catch {
            /* idem */
        }
    }

    /** Fecha a última parte, solta o microfone e espera as partes serem entregues. */
    async concluir(): Promise<void> {
        if (!this.ativo) return this.fila;
        this.ativo = false;
        this.pararRelogio();
        await this.fecharParte(false);
        this.soltarMicrofone();
        return this.fila;
    }

    /** Joga a parte atual fora e solta o microfone. */
    cancelar(): void {
        this.ativo = false;
        this.pararRelogio();
        const g = this.gravador;
        this.gravador = null;
        this.pedacos = [];
        if (g) {
            g.ondataavailable = null;
            try {
                if (g.state !== 'inactive') g.stop();
            } catch {
                /* já parado */
            }
        }
        this.soltarMicrofone();
    }

    private comecarParte(): void {
        if (!this.fluxo) return;
        const formato = melhorFormato();
        const g = formato ? new MediaRecorder(this.fluxo, { mimeType: formato }) : new MediaRecorder(this.fluxo);
        this.pedacos = [];
        g.ondataavailable = (ev) => {
            if (ev.data && ev.data.size > 0) this.pedacos.push(ev.data);
        };
        g.start();
        this.gravador = g;
        this.acumulado = 0;
        this.desde = this.pausado ? null : this.agora();
        if (this.pausado) {
            try {
                g.pause();
            } catch {
                /* idem */
            }
        }
    }

    private conferirTempo(): void {
        if (!this.ativo || this.pausado) return;
        if (this.segundosDaParte() * 1000 >= this.opcoes.parteMs) void this.fecharParte(true);
    }

    private async fecharParte(continuar: boolean): Promise<void> {
        const g = this.gravador;
        if (!g) return;
        const segundos = Math.round(this.segundosDaParte() * 10) / 10;
        const ordem = this.ordem;
        this.gravador = null;
        const pedacos = this.pedacos;
        const parado = new Promise<void>((resolve) => {
            if (g.state === 'inactive') {
                resolve();
                return;
            }
            const fim = setTimeout(resolve, ESPERA_DO_FIM_MS);
            g.addEventListener('stop', () => {
                clearTimeout(fim);
                resolve();
            }, { once: true });
            try {
                if (g.state === 'paused') g.resume();
                g.stop();
            } catch {
                clearTimeout(fim);
                resolve();
            }
        });
        /* A próxima começa já: a emenda perde só o tempo de o navegador abrir outro gravador. */
        if (continuar && this.ativo) {
            this.ordem += 1;
            this.comecarParte();
        }
        await parado;
        g.ondataavailable = null;
        const audio = new Blob(pedacos, { type: g.mimeType || pedacos[0]?.type || 'audio/webm' });
        /* Parte vazia não vai: um buraco na numeração não atrapalha, a leitura é por ordem. */
        if (audio.size < MENOR_PARTE_BYTES || segundos < MENOR_PARTE_SEGUNDOS) return;
        if (!continuar) this.ordem += 1;
        this.fila = this.fila.then(() => this.opcoes.aoFecharParte({ ordem, segundos, audio }));
        await this.fila;
    }

    /** A próxima ordem livre, para continuar uma nota depois de recarregar. */
    proximaOrdem(): number {
        return this.ordem;
    }

    private pararRelogio(): void {
        if (this.relogio) clearInterval(this.relogio);
        this.relogio = null;
    }

    private soltarMicrofone(): void {
        this.fluxo?.getTracks().forEach((t) => {
            t.onended = null;
            t.stop();
        });
        this.fluxo = null;
    }
}
